<template>
    <div class="requests-bell">
        <v-menu v-model="menu" offset-y left :close-on-content-click="false" max-width="440" min-width="340">
            <template v-slot:activator="{ on, attrs }">
                <v-btn icon class="mr-1" title="Requests" v-bind="attrs" v-on="on">
                    <v-badge :value="count > 0" :content="count" color="primary" overlap>
                        <v-icon color="white">{{ count ? 'mdi-bell-ring-outline' : 'mdi-bell-outline' }}</v-icon>
                    </v-badge>
                </v-btn>
            </template>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold py-3">
                    <v-icon small color="primary" class="mr-2">mdi-bell-outline</v-icon>Requests
                    <v-spacer />
                    <v-btn x-small text to="/requests" @click="menu = false">View all</v-btn>
                </v-card-title>
                <v-divider />
                <div v-if="!count" class="pa-4 text-body-2 grey--text">No new requests</div>
                <div v-else class="requests-bell__list">
                    <!-- team applications from the website, waiting to be reviewed -->
                    <template v-for="application in applications">
                        <div :key="`application-${application.id}`" class="requests-bell__item">
                            <div class="requests-bell__type">
                                <v-icon x-small color="green darken-1">mdi-account-group-outline</v-icon>
                                Team application · <span class="green--text text--darken-1">New</span>
                            </div>
                            <div class="font-weight-bold">{{ application.teamName }}</div>
                            <div class="text-caption grey--text text--darken-1">
                                {{ playersCount(application) }} players
                                <span v-if="application.telephone"> · {{ application.telephone }}</span>
                                <span v-if="application.email"> · {{ application.email }}</span>
                            </div>
                            <div class="d-flex mt-2">
                                <v-btn x-small depressed color="black" class="white--text mr-2" :to="`/team-applications/${application.id}`" @click="menu = false">
                                    <v-icon x-small left>mdi-eye-outline</v-icon>Review
                                </v-btn>
                                <v-btn x-small text to="/team-applications" @click="menu = false">All applications</v-btn>
                            </div>
                        </div>
                    </template>

                    <!-- match changes: every team agreed (to decide) or declined / expired (to acknowledge) -->
                    <template v-for="request in matchChanges">
                        <div :key="`change-${request.id}`" class="requests-bell__item">
                            <div class="requests-bell__type">
                                <v-icon x-small :color="statusColor(request)">mdi-swap-horizontal</v-icon>
                                Match change ·
                                <span :class="`${statusColor(request)}--text`">{{ statusLabel(request) }}</span>
                            </div>
                            <match-change-summary :request="request" />
                            <div class="d-flex mt-2">
                                <template v-if="request.status === 'pending'">
                                    <v-btn x-small depressed color="black" class="white--text mr-2" @click="confirm('change', request, true)">
                                        <v-icon x-small left>mdi-check</v-icon>Approve
                                    </v-btn>
                                    <v-btn x-small outlined color="red darken-1" class="mr-2" @click="confirm('change', request, false)">
                                        <v-icon x-small left>mdi-close</v-icon>Reject
                                    </v-btn>
                                </template>
                                <v-btn v-else x-small outlined class="mr-2" @click="markSeen(request)">
                                    <v-icon x-small left>mdi-check</v-icon>OK
                                </v-btn>
                            </div>
                        </div>
                    </template>

                    <!-- postponements waiting for a decision -->
                    <template v-for="request in postponements">
                        <div :key="`postponement-${request.id}`" class="requests-bell__item">
                            <div class="requests-bell__type">
                                <v-icon x-small color="orange">mdi-calendar-remove-outline</v-icon>
                                Postponement · <span class="orange--text">Waiting for approval</span>
                            </div>
                            <div class="font-weight-bold">{{ request.team1Name }} - {{ request.team2Name }}</div>
                            <div class="text-caption grey--text text--darken-1">
                                {{ formatMatchDate(request.matchDatetime) }}<span v-if="request.arenaName"> · {{ request.arenaName }}</span>
                            </div>
                            <div class="text-body-2 mt-1">
                                Requested by <b>{{ request.teamName }}</b>
                                <span v-if="request.playerName">(captain {{ request.playerName }})</span>
                                · {{ formatAgo(request.createdAt) }}
                            </div>
                            <div v-if="request.reason" class="text-body-2 mt-1 grey--text text--darken-2">"{{ request.reason }}"</div>
                            <div v-if="request.matchPostponed" class="text-caption mt-1 grey--text">The match is already postponed</div>
                            <div class="d-flex mt-2">
                                <v-btn x-small depressed color="black" class="white--text mr-2" @click="confirm('postponement', request, true)">
                                    <v-icon x-small left>mdi-check</v-icon>Approve
                                </v-btn>
                                <v-btn x-small outlined color="red darken-1" class="mr-2" @click="confirm('postponement', request, false)">
                                    <v-icon x-small left>mdi-close</v-icon>Reject
                                </v-btn>
                                <v-btn x-small text :to="`/matches/${request.matchId}/edit`" @click="menu = false">Match</v-btn>
                            </div>
                        </div>
                    </template>
                </div>
            </v-card>
        </v-menu>

        <request-review-dialog ref="review" @done="onReviewed" />

        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="6000" top right>
            {{ snackbarText }}
            <template v-slot:action="{ attrs }">
                <v-btn v-if="snackbarOpensMenu" text v-bind="attrs" @click="openMenu">View</v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
    import MatchChangeSummary from '@/components/MatchChangeSummary'
    import RequestReviewDialog from '@/components/RequestReviewDialog'
    import postponementService from '@/services/postponement'
    import matchChangeService from '@/services/match-change'
    import teamApplicationService from '@/services/team-application'
    import { MATCH_CHANGE_STATUS, errorMessage, formatAgo, formatMatchDate, slotTeams } from '@/utils/requests'

    const POLL_INTERVAL = 60 * 1000

    // The requests of the team captains (made on the website / apps), polled while the CMS is open:
    // postponements and match changes to approve, match changes a team declined or that expired,
    // and the team applications (registrations) still pending.
    export default {
        name: 'RequestNotifications',
        components: { MatchChangeSummary, RequestReviewDialog },
        data() {
            return {
                postponements: [],
                matchChanges: [],
                applications: [],
                // keys seen by the previous poll: a new key is a new notification to tell the user about
                knownKeys: null,
                menu: false,
                interval: null,
                snackbar: false,
                snackbarText: '',
                snackbarColor: 'black',
                snackbarOpensMenu: false
            }
        },
        computed: {
            count() {
                return this.postponements.length + this.matchChanges.length + this.applications.length
            }
        },
        created() {
            this.poll()
            this.interval = setInterval(this.poll, POLL_INTERVAL)
            document.addEventListener('visibilitychange', this.onVisibilityChange)
            this.$root.$on('requests-changed', this.poll)
        },
        beforeDestroy() {
            clearInterval(this.interval)
            document.removeEventListener('visibilitychange', this.onVisibilityChange)
            this.$root.$off('requests-changed', this.poll)
        },
        methods: {
            formatAgo,
            formatMatchDate,
            poll() {
                // no need to poll a hidden tab: it polls again once visible
                if (document.hidden) {
                    return
                }
                Promise.all([
                    postponementService.getPendingPostponements().then(resp => resp.data || []),
                    matchChangeService.getMatchChangeNotifications().then(resp => resp.data || []),
                    // a failing list of applications must not hide the other requests
                    teamApplicationService.getFilteredTeamApplications({ status: 'pending' }, 1, 50)
                        .then(resp => (resp.data && resp.data.content) || [])
                        .catch(() => this.applications)
                ]).then(([postponements, matchChanges, applications]) => {
                    const items = applications.map(application => ({ key: `a-${application.id}`, text: `New team application: ${application.teamName}` }))
                        .concat(postponements.map(request => ({ key: `p-${request.id}`, text: `Postponement: ${request.team1Name} - ${request.team2Name}` }))
                            .concat(matchChanges.map(request => ({
                                key: `c-${request.id}-${request.status}`,
                                text: `Match change ${this.statusLabel(request).toLowerCase()}: ${slotTeams(request.match1)} ⇄ ${slotTeams(request.match2)}`
                            }))))
                    if (this.knownKeys) {
                        const fresh = items.filter(item => !this.knownKeys.includes(item.key))
                        if (fresh.length) {
                            this.notify(fresh.length === 1 ? fresh[0].text : `${fresh.length} new requests`, 'primary darken-1', true)
                        }
                    }
                    this.knownKeys = items.map(item => item.key)
                    this.postponements = postponements
                    this.matchChanges = matchChanges
                    this.applications = applications
                }).catch(error => console.error('Could not load the requests', error))
            },
            onVisibilityChange() {
                if (!document.hidden) {
                    this.poll()
                }
            },
            playersCount(application) {
                return Array.isArray(application.players) ? application.players.length : 0
            },
            statusLabel(request) {
                return (MATCH_CHANGE_STATUS[request.status] || {}).label || request.status
            },
            statusColor(request) {
                return (MATCH_CHANGE_STATUS[request.status] || {}).color || 'grey'
            },
            confirm(type, request, approve) {
                this.$refs.review.open(type, request, approve)
            },
            onReviewed({ ok, message }) {
                this.notify(message, ok ? 'success' : 'error', false)
                this.$root.$emit('requests-changed')
            },
            markSeen(request) {
                matchChangeService.markMatchChangeSeen(request.id).then(() => {
                    this.$root.$emit('requests-changed')
                }).catch(error => this.notify(errorMessage(error), 'error', false))
            },
            notify(text, color, opensMenu) {
                this.snackbarText = text
                this.snackbarColor = color
                this.snackbarOpensMenu = opensMenu
                this.snackbar = true
            },
            openMenu() {
                this.snackbar = false
                this.menu = true
            }
        }
    }
</script>

<style scoped>
.requests-bell__list {
    max-height: 65vh;
    overflow-y: auto;
}

.requests-bell__item {
    padding: 12px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    white-space: normal;
}

.requests-bell__item:last-child {
    border-bottom: 0;
}

.requests-bell__type {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 4px;
}

.requests-bell .v-btn {
    text-transform: none;
    letter-spacing: normal;
}
</style>
